import BannerBackground from '@assets/icons/ReferralJourneyBanner.svg'
import StackedCoinWithStar from '@assets/icons/StackedCoinWithStar.svg'
import CrossIcon from '@components/CrossIcon/CrossIcon'
import LinearProgress from '@components/LinearProgress/LinearProgress'
import { ModalWithIcon } from '@components/ModalWithIcon/ModalWithIcon'
import { joinArrayOfStringIntoString, lowerCaseFirstWord, toCurrencyFormat } from '@lib/utils'
import { useReferralBannerStore } from '@stores/referralBannerStore'
import ReactDOM from 'react-dom'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { RefereeInfo } from '../../types/referral'
import {
    Container,
    BackgroundImage,
    Overlay,
    HeaderContainer,
    InfoContainer,
    TextContainer,
    TitleText,
    SubtitleText,
    CloseButton,
    ProgressContainer,
    StepText,
    StepTextContent,
    ModalContainer
} from './ReferralJourneyBanner.styles'

const ReferralJourneyBanner = ({
    refereeInfo,
    animationDuration = 0.5,
}: {
    refereeInfo: RefereeInfo
    animationDuration?: number
}) => {
    const navigate = useNavigate()
    const [isModalVisible, setIsModalVisible] = useState(false)
    const { dismissBanner } = useReferralBannerStore()
    const { t } = useTranslation()

    const reward = refereeInfo.program_referee_reward_amount
    const stepsArray = refereeInfo.steps.map(s => lowerCaseFirstWord(s.description))
    const rewardRequirements = joinArrayOfStringIntoString(stepsArray)
    const completedStep = refereeInfo.steps_completed
    const totalSteps = refereeInfo.max_steps

    return (
        <Container 
            $animationDuration={animationDuration}
            onClick={() => navigate('/claim-reward', { state: { refereeInfo: JSON.stringify(refereeInfo) } })}>
            <BackgroundImage src={BannerBackground} />
            <Overlay />
            <HeaderContainer>
                <InfoContainer>
                    <img src={StackedCoinWithStar} alt={t('components.referralJourneyBanner.stackedCoinAlt')} />
                    <TextContainer>
                        <TitleText>{t('components.referralJourneyBanner.claimReward')}</TitleText>
                        <SubtitleText>
                            {t('components.referralJourneyBanner.claimSteps', { amount: toCurrencyFormat(reward) })}
                        </SubtitleText>
                    </TextContainer>
                </InfoContainer>
                <CloseButton
                    onClick={(e) => {
                        e.stopPropagation()
                        setIsModalVisible(true)
                    }}
                >
                    <CrossIcon 
                        color="#FFFFFF"
                        thickness={1.5}
                        size={16}
                    />
                </CloseButton>
            </HeaderContainer>
            <ProgressContainer>
                <LinearProgress progress={completedStep / totalSteps} />
            </ProgressContainer>
            <StepText>
                <StepTextContent>
                    {t('components.referralJourneyBanner.stepCounter', { completed: completedStep, total: totalSteps })}
                </StepTextContent>
            </StepText>
            {ReactDOM.createPortal(
                <ModalContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <ModalWithIcon 
                        type='void'
                        title={t('components.referralJourneyBanner.modalTitle')}
                        description={t('components.referralJourneyBanner.modalDescription', { requirements: rewardRequirements })}
                        onPositive={() => {
                            dismissBanner()
                            setIsModalVisible(false)
                        }}
                        positiveText={t('components.referralJourneyBanner.confirmButton')}
                        visible={isModalVisible}
                        onClose={() => {setIsModalVisible(false)}}
                        closeButton
                    />
                </ModalContainer>,
                document.body
            )}
        </Container>
    )
}   

export default ReferralJourneyBanner
